import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { API, axios, showToast } from './adminHelpers';

export const SettingsManager = ({ authHeader }) => {
  const [siteInfo, setSiteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/site-info`, { headers: authHeader });
        setSiteInfo(res.data);
      } catch (err) {
        console.error('Failed to fetch site info:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [authHeader]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-info`, siteInfo, { headers: authHeader });
      showToast('Settings saved!');
    } catch {
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSiteInfo(prev => ({ ...prev, [field]: value }));
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="settings-manager">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
        <Button onClick={saveSettings} disabled={saving} className="bg-primary hover:bg-primary-hover" data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold mb-4">Business Information</h2>
          <div className="space-y-4">
            {[
              { field: 'business_name', label: 'Business Name' },
              { field: 'tagline', label: 'Tagline' },
              { field: 'phone', label: 'Phone Number' },
              { field: 'email', label: 'Email Address' },
              { field: 'address', label: 'Address' },
              { field: 'abn', label: 'ABN' },
            ].map(({ field, label }) => (
              <div key={field}>
                <Label>{label}</Label>
                <Input value={siteInfo?.[field] || ''} onChange={(e) => updateField(field, e.target.value)} className="mt-1" data-testid={`settings-${field}`} />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold mb-4">Social Media</h2>
          <div className="space-y-4">
            {[
              { field: 'social_facebook', label: 'Facebook URL', placeholder: 'https://facebook.com/...' },
              { field: 'social_instagram', label: 'Instagram URL', placeholder: 'https://instagram.com/...' },
              { field: 'social_twitter', label: 'Twitter URL', placeholder: 'https://twitter.com/...' },
              { field: 'social_linkedin', label: 'LinkedIn URL', placeholder: 'https://linkedin.com/...' },
            ].map(({ field, label, placeholder }) => (
              <div key={field}>
                <Label>{label}</Label>
                <Input value={siteInfo?.[field] || ''} onChange={(e) => updateField(field, e.target.value)} placeholder={placeholder} className="mt-1" data-testid={`settings-${field}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
